// =============================================================================
// Contact Form Modal
// =============================================================================
// Modal for creating and editing CRM contacts
// =============================================================================

import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError, formatPhoneInput, stripPhoneFormat } from '../../../services/api';
import type {
  Contact,
  CreateContactInput,
  LeadSource,
  AccreditationStatus,
} from '../../../types/fundraising';
import {
  LEAD_SOURCE_LABELS,
  ACCREDITATION_STATUS_LABELS,
  PROPERTY_TYPE_LABELS,
} from '../../../types/fundraising';
import type { PropertyType } from '../../../types';

// =============================================================================
// Types
// =============================================================================

interface ContactFormModalProps {
  isOpen: boolean;
  contact?: Contact | null; // If provided, edit mode
  onClose: () => void;
  onSaved: () => void;
}

// =============================================================================
// Constants
// =============================================================================

const LEAD_SOURCES: LeadSource[] = [
  'REFERRAL',
  'WEBSITE',
  'EVENT',
  'BROKER',
  'LINKEDIN',
  'COLD_OUTREACH',
  'EXISTING_INVESTOR',
  'OTHER',
];

const ACCREDITATION_STATUSES: AccreditationStatus[] = [
  'NOT_VERIFIED',
  'SELF_CERTIFIED',
  'DOCUMENT_VERIFIED',
  'THIRD_PARTY_VERIFIED',
  'EXPIRED',
];

const PROPERTY_TYPES: PropertyType[] = [
  'MULTIFAMILY',
  'OFFICE',
  'RETAIL',
  'INDUSTRIAL',
  'MIXED_USE',
  'HOTEL',
  'SELF_STORAGE',
  'OTHER',
];


// =============================================================================
// Component
// =============================================================================

export function ContactFormModal({ isOpen, contact, onClose, onSaved }: ContactFormModalProps) {
  const isEditMode = !!contact;

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [leadSource, setLeadSource] = useState<LeadSource>('REFERRAL');
  const [referredBy, setReferredBy] = useState('');
  const [referralNotes, setReferralNotes] = useState('');
  const [accreditationStatus, setAccreditationStatus] = useState<AccreditationStatus>('NOT_VERIFIED');
  const [accreditationMethod, setAccreditationMethod] = useState('');
  const [investmentCapacity, setInvestmentCapacity] = useState('');
  const [propertyInterests, setPropertyInterests] = useState<PropertyType[]>([]);
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (isOpen) {
      if (contact) {
        // Edit mode - populate form
        setFirstName(contact.firstName);
        setLastName(contact.lastName);
        setEmail(contact.email);
        setPhone(contact.phone ? formatPhoneInput(contact.phone) : '');
        setCompany(contact.company || '');
        setTitle(contact.title || '');
        setAddress(contact.address || '');
        setCity(contact.city || '');
        setState(contact.state || '');
        setZipCode(contact.zipCode || '');
        setLeadSource(contact.leadSource);
        setReferredBy(contact.referredBy || '');
        setReferralNotes(contact.referralNotes || '');
        setAccreditationStatus(contact.accreditationStatus || 'NOT_VERIFIED');
        setAccreditationMethod(contact.accreditationMethod || '');
        setInvestmentCapacity(contact.investmentCapacity || '');
        setPropertyInterests(contact.propertyInterests || []);
        setTags(contact.tags?.join(', ') || '');
        setNotes(contact.notes || '');
      } else {
        // Create mode - reset form
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setCompany('');
        setTitle('');
        setAddress('');
        setCity('');
        setState('');
        setZipCode('');
        setLeadSource('REFERRAL');
        setReferredBy('');
        setReferralNotes('');
        setAccreditationStatus('NOT_VERIFIED');
        setAccreditationMethod('');
        setInvestmentCapacity('');
        setPropertyInterests([]);
        setTags('');
        setNotes('');
      }
      setError(null);
    }
  }, [isOpen, contact]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhoneInput(e.target.value));
  };

  const togglePropertyInterest = (type: PropertyType) => {
    setPropertyInterests((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const phoneDigits = stripPhoneFormat(phone);
      const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);

      const data: CreateContactInput = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phoneDigits || undefined,
        company: company.trim() || undefined,
        title: title.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zipCode: zipCode.trim() || undefined,
        leadSource,
        referredBy: referredBy.trim() || undefined,
        referralNotes: referralNotes.trim() || undefined,
        accreditationStatus,
        accreditationMethod: accreditationMethod.trim() || undefined,
        investmentCapacity: investmentCapacity.trim() || undefined,
        propertyInterests: propertyInterests.length > 0 ? propertyInterests : undefined,
        tags: tagList.length > 0 ? tagList : undefined,
        notes: notes.trim() || undefined,
      };

      if (isEditMode && contact) {
        await fundraisingService.updateContact(contact.id, data);
      } else {
        await fundraisingService.createContact(data);
      }

      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Edit Contact' : 'Add New Contact'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="123-456-7890"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Address</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    maxLength={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Lead Source */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Lead Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="leadSource" className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Source *
                </label>
                <select
                  id="leadSource"
                  value={leadSource}
                  onChange={(e) => setLeadSource(e.target.value as LeadSource)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {LEAD_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {LEAD_SOURCE_LABELS[source]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="referredBy" className="block text-sm font-medium text-gray-700 mb-1">
                  Referred By
                </label>
                <input
                  type="text"
                  id="referredBy"
                  value={referredBy}
                  onChange={(e) => setReferredBy(e.target.value)}
                  placeholder="Name of referrer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="col-span-2">
                <label htmlFor="referralNotes" className="block text-sm font-medium text-gray-700 mb-1">
                  Referral Notes
                </label>
                <input
                  type="text"
                  id="referralNotes"
                  value={referralNotes}
                  onChange={(e) => setReferralNotes(e.target.value)}
                  placeholder="How they were referred"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Accreditation */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Accreditation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="accreditationStatus" className="block text-sm font-medium text-gray-700 mb-1">
                  Accreditation Status
                </label>
                <select
                  id="accreditationStatus"
                  value={accreditationStatus}
                  onChange={(e) => setAccreditationStatus(e.target.value as AccreditationStatus)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {ACCREDITATION_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {ACCREDITATION_STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="accreditationMethod" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Method
                </label>
                <input
                  type="text"
                  id="accreditationMethod"
                  value={accreditationMethod}
                  onChange={(e) => setAccreditationMethod(e.target.value)}
                  placeholder="e.g., Letter from CPA"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="investmentCapacity" className="block text-sm font-medium text-gray-700 mb-1">
                  Investment Capacity
                </label>
                <input
                  type="text"
                  id="investmentCapacity"
                  value={investmentCapacity}
                  onChange={(e) => setInvestmentCapacity(e.target.value)}
                  placeholder="e.g., $100K-500K"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Property Interests */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Property Interests</h3>
            <div className="flex flex-wrap gap-2">
              {PROPERTY_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => togglePropertyInterest(type)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    propertyInterests.includes(type)
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {PROPERTY_TYPE_LABELS[type] || type}
                </button>
              ))}
            </div>
          </div>

          {/* Tags & Notes */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags & Notes</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  id="tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="Comma-separated tags (e.g., VIP, Family Office)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Additional notes about this contact"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Contact'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactFormModal;
